import { css } from '@emotion/react'
import { zodResolver } from '@hookform/resolvers/zod'
import { InputTextControlled } from '@nexans/react-form'
import { Button, ServiceLogo, Stack, Typography } from '@nexans/react-ui-kit'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { RESEND_TIME } from '@/config'
import { ROUTER_PRIVATE_LINKS, ROUTER_PUBLIC_LINKS } from '@/router/config'
import { useAppStore } from '@/stores/appStore'
import { PublicDto } from '@/types'

import { useResendUserActivation } from '../api/useResendUserActivation'
import { useVerifyUser } from '../api/useVerifyUser'
import { VerifyUserSchema } from '../schemas'

export interface AuthOtpState {
  email: string
  origin: 'signin' | 'register'
}

export function CodeVerification() {
  const { t } = useTranslation(['auth', 'common', 'errors'])
  const sessionId = useAppStore((state) => state.sessionId)
  const location = useLocation()
  const navigate = useNavigate()
  const state = location.state as AuthOtpState | null
  const [resendTimer, setResendTimer] = useState(RESEND_TIME)
  const origin = state?.origin ?? 'signin'
  const titleKey = origin === 'register' ? 'auth:labels.otp.register.title' : 'auth:labels.otp.title'
  const introductionKey = origin === 'register' ? 'auth:labels.otp.register.introduction' : 'auth:labels.otp.introduction'

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<PublicDto['VerifyUserCommand']>({
    defaultValues: {
      sessionId: sessionId,
      code: '',
    },
    resolver: zodResolver(VerifyUserSchema),
    mode: 'onChange',
  })

  const { mutate: verifyUser, isPending: isVerifyUserPending } = useVerifyUser({
    config: {
      onSuccess: () => navigate(ROUTER_PRIVATE_LINKS.home.path),
    },
  })

  const { mutate: resendUserActivation, isPending: isResendPending } = useResendUserActivation({
    config: {
      onSuccess: () => {
        setResendTimer(RESEND_TIME)
      },
    },
  })

  useEffect(() => {
    if (!state?.email) {
      navigate(ROUTER_PUBLIC_LINKS.authSignin.path, { replace: true })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Timer for resend code button
  useEffect(() => {
    if (resendTimer <= 0) return

    const interval = setInterval(() => {
      setResendTimer((prev) => prev - 1)
    }, 1000)

    return () => {
      clearInterval(interval)
    }
  }, [resendTimer])

  if (!state?.email) return null

  const isLoading = isVerifyUserPending || isResendPending

  const handleUserKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isValid && !isLoading) {
      handleSubmit(onSubmit)()
    }
  }

  const onSubmit = (data: PublicDto['VerifyUserCommand']) => {
    verifyUser(data)
  }

  const handleResendCode = () => {
    resendUserActivation({ sessionId: sessionId! })
  }

  return (
    <Stack fullHeight fullWidth>
      <ServiceLogo css={{ alignSelf: 'center', marginTop: 24 }} iconSrc={undefined} iconSrcScale={1} size={40} title="Mobiway™" />

      <Stack css={{ flex: 1 }} justify="center" spacing="lg" fullWidth>
        <Typography css={{ marginBottom: 24 }} textAlign="center" variant="h2">
          {t(titleKey)}
        </Typography>

        <Typography textAlign="justify" variant="subtitle1">
          {t(introductionKey, { email: state.email })}
        </Typography>

        <InputTextControlled
          control={control}
          errorText={errors.code?.message ? t(`errors:${errors.code?.message}`) : ''}
          gutterBottom={!!errors.code}
          label={t('auth:labels.otp.code')}
          name="code"
          fullWidth
          required
          onKeyDown={handleUserKeyPress}
        />

        <Button color="primary" disabled={resendTimer > 0} loading={isLoading} variant="text" onClick={handleResendCode}>
          {resendTimer > 0 ? t('auth:actions.resend_code_waiting', { time: resendTimer }) : t('auth:actions.resend_code')}
        </Button>

        <Stack mt="xl" fullWidth>
          <Button
            color="gradient"
            css={styles.gradientButton}
            disabled={!isValid || isLoading}
            variant="contained"
            onClick={handleSubmit(onSubmit)}
          >
            {t('auth:actions.signin_me')}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  )
}

const styles = {
  gradientButton: css({
    '&:not(:disabled)': { filter: 'drop-shadow(0 16px 32px rgba(255, 0, 0, 0.35))' },
  }),
}
